/** @prettier */

import React from 'react';
import {useState, useEffect, useContext, memo} from 'react';
import {Grid, Box, Stack, IconButton, Chip} from '@mui/material';
import {isUndefined} from 'lodash';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {GraphButton, ControlButton} from './SiteSubContentsIconButton';
//API import
import {
  getStatesSitesGroups,
  getCompositionsGroups,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {GraphFormType, GraphTargetType, mysite} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import * as mySiteLib from './mySiteLibrary';
import {ContextApplyFilterTab} from './MySiteArea';
import {iconBlack, iconWhite, iconRed, limit} from '../../define';
import {numberWithPeriod, roundOff} from '../../functions/convertString';

//グループ一覧の幅一覧
//変更した場合、ホバーした際のボタンのレイアウトを確認すること
const gridXsAirGroup = 3.5;
const gridXsAirUnit = 3.5;
const gridXsAirKwh = 4;
const gridXsAirArrow = 1;

/**
 * マイサイト操作画面（グループ）
 * @param props props
 * @returns JSX
 */
const ViewGroup = memo(
  (props: {
    siteProfile: apiIF.resParameters.siteInformations;
    floorFilter: string;
    setGraphParam: (
      graphTarget: GraphTargetType,
      graphTargetID?: string,
      graphType?: GraphFormType,
      graphTitle?: string
    ) => void;
    setOperationParam: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
  }) => {
    const trans = useTranslation().t; //翻訳関数

    //prettier-ignore
    //グループの情報
    const [viewGroupData, setViewGroupData] = useState<apiIF.resParameters.groupStates[]>([]);
    //prettier-ignore
    //グループの消費電力量
    const [viewGroupPwData, setViewGroupPwData] = useState<mysite.groupPwData[]>([]);
    //prettier-ignore
    //グループ表示用リスト この配列の順番に画面描画される
    const [groupFloorList, setGroupFloorList] = useState<mysite.groupFloorList[]>([]);
    //prettier-ignore
    //グループごとの権限
    const [groupAuthority, setGroupAuthority] = useState<{[groupID: string]: 'manager' | 'viewer' | 'none'}>({});
    //選択状態のグループ
    const [selectedGroup, setSelectedGroup] = useState('');

    /** サイトID */
    const siteId = props.siteProfile['site-id'];
    /** APIで問い合わせる */
    const afterTime =
      props.siteProfile['timezone'] === undefined
        ? undefined
        : mySiteLib.timezone2aftertime(props.siteProfile['timezone']);

    //最新グループ状態値取得用
    useEffect(() => {
      /**
       * グループ名を降順にソート
       * @param groupData グループ受信データ
       * @returns フロア階層
       */
      const sortGroupList = (groupData: apiIF.resParameters.groupStates[]) => {
        //ソートで配列が破壊されるので、配列をコピー
        let localGroupData = groupData.concat();

        //フロア階層とグループ名でソート
        localGroupData.sort((a, b) => {
          //先にフロア階数で降順ソート
          if (Number(a.floor) !== Number(b.floor)) {
            return Number(a.floor) > Number(b.floor) ? -1 : 1;
          }

          //フロア階層ソート後にグループ名で昇順ソート
          if (a['group-name'] !== b['group-name']) {
            return a['group-name'] < b['group-name'] ? -1 : 1;
          }
          return 0;
        });

        /** グループフロアリスト */
        let localGroupFloorList: mysite.groupFloorList[] = [];

        //グループフロアリストにソートされたフロアとグループIDを追加する
        //前処理でlocalGroupDataのデータはソート済
        localGroupData.forEach(groupOneData => {
          const floor = groupOneData['floor'];
          const groupId = groupOneData['group-id'];

          //フロアグループリストにフロアがあるか検索し、無ければ新規追加
          if (
            localGroupFloorList.some(element => element['floor'] === floor) ===
            false
          ) {
            localGroupFloorList.push({floor: floor, groupIds: [groupId]});
          } else {
            //フロアがあれば、グループIDを追加する
            localGroupFloorList.forEach(element => {
              if (element['floor'] === floor) {
                element['groupIds'].push(groupId);
              }
            });
          }
        });

        return localGroupFloorList;
      };

      /**
       * グループデータ取得
       */
      const reqStatesSitesGroups = () => {
        //最新の状態値を取得（消費電力量は必要ないが、全取得するため空白とする）
        const query: apiIF.reqParameters.getStatesSitesFloors = {};
        getStatesSitesGroups(siteId, query)
          .then(response => {
            //最新の稼働状況データ (配列の先頭が最新データ)
            setViewGroupData(response['group-states'][0]['states']);
            //グループリストをソートする
            setGroupFloorList(
              sortGroupList(response['group-states'][0]['states'])
            );
          })
          .catch(err => {});
      };

      //ページ遷移時初回読み込み
      reqStatesSitesGroups();
      //定期実行
      const intervalId = setInterval(() => {
        reqStatesSitesGroups();
      }, 60000);
      return function () {
        clearInterval(intervalId);
      };
    }, [siteId]);

    //消費電力取得用
    //１日分のデータ処理はAPIの応答に時間がかかるので、消費電力量だけ分離する
    useEffect(() => {
      /**
       * グループ別種別消費電力量
       * @param recvGroupData グループ情報
       * @return グループ別消費電力量
       */
      const calcGroupPwData = (
        recvGroupData: apiIF.resParameters.groupStates[]
      ) => {
        /**
         * API戻り値の消費電力量WをKW変換(小数点以下四捨五入)
         */
        const convNumKw = mySiteLib.convNumKw;

        return recvGroupData.map(groupData => {
          //受信データに消費電力がない場合は、0とする
          if (isUndefined(groupData['power-consumption'])) {
            return {
              'group-id': groupData['group-id'],
              'power-consumption': '0',
            };
          }
          //グループ別消費電力量の、冷房・暖房・その他を合計する
          const cooling = convNumKw(
            groupData['power-consumption']['cooling-mode']
          );
          const heating = convNumKw(
            groupData['power-consumption']['heating-mode']
          );
          const other = convNumKw(groupData['power-consumption']['other-mode']);
          const pwData = String(cooling + heating + other);
          return {
            'group-id': groupData['group-id'],
            'power-consumption': pwData,
          };
        });
      };

      /**
       * フロアデータ取得
       */
      const reqStatesSitesFloorsPw = () => {
        //最新データ取得 (消費電力量取得)
        const query: apiIF.reqParameters.getStatesSitesFloors = {
          'after-time': afterTime,
          'fields': 'power-consumption',
          'every-minute': 1440, //60min × 24h  １日分のデータ取得
        };

        getStatesSitesGroups(siteId, query)
          .then(response => {
            //最新の稼働状況データ (配列の先頭が最新データ)
            setViewGroupPwData(
              calcGroupPwData(response['group-states'][0]['states'])
            );
          })
          .catch(err => {});
      };

      //ページ遷移時初回読み込み
      reqStatesSitesFloorsPw();
      //定期実行
      const intervalId = setInterval(() => {
        reqStatesSitesFloorsPw();
      }, 60000);
      return function () {
        clearInterval(intervalId);
      };
    }, [siteId, afterTime]);

    //グループの権限取得用
    useEffect(() => {
      getCompositionsGroups(siteId, {
        'fields': 'group-authority',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      }).then(response => {
        //フロアごとの権限を取得
        const authority = response['group-informations'].reduce(
          (result, groupInfo) => {
            if (groupInfo['group-authority'] === undefined) {
              return result;
            }

            result[groupInfo['group-id']] = groupInfo['group-authority'];
            return result;
          },
          {} as {
            [groupID: string]: 'manager' | 'viewer' | 'none';
          }
        );
        setGroupAuthority(authority);
      });
    }, [siteId]);

    /**
     * フロア名変換テーブル
     * @param floor フロア
     * @returns フロア名
     */
    const convertFloorName = (floor: string) => {
      switch (floor) {
        case '14':
        case '13': //fall through
        case '12': //fall through
        case '11': //fall through
        case '10': //fall through
        case '9': //fall through
        case '8': //fall through
        case '7': //fall through
        case '6': //fall through
        case '5': //fall through
        case '4': //fall through
        case '3': //fall through
        case '2': //fall through
        case '1': //fall through
        case '0': //fall through
          return floor;
        case '-1':
          return 'B1';
        case '-2':
          return 'B2';
        case '-3':
          return 'B3';
        case '-4':
          return 'B4';
        default:
          return '';
      }
    };

    return (
      <>
        {/** フィルタ情報 */}
        <Box className="subContents__content__filterChip">
          <FilterButton floorFilter={props.floorFilter} />
        </Box>
        {/** ヘッダ */}
        <Grid
          container
          alignItems="center"
          className="subContents__content__header">
          <Grid
            item
            xs={gridXsAirGroup}
            className="subContents__content__header__category">
            {trans('ViewGroup.header.floorGroup')}
          </Grid>
          <Grid
            item
            xs={gridXsAirUnit}
            className="subContents__content__header__status">
            {trans('ViewGroup.header.status')}
          </Grid>
          <Grid
            item
            xs={gridXsAirKwh + gridXsAirArrow}
            className="subContents__content__header__power">
            {trans('ViewGroup.header.consumption')}
          </Grid>
        </Grid>
        {/** フロアごとにグループを出力 */}
        <Box className="subContents__content__body">
          <Stack>
            {groupFloorList
              //指定したフロアのみでフィルタする
              .filter(groupInfo => {
                return (
                  props.floorFilter === '' ||
                  groupInfo.floor === props.floorFilter
                );
              })
              .map((groupInfo, index) => {
                return (
                  <Grid
                    container
                    key={`GroupViewData${groupInfo.floor}`}
                    className={
                      index === 0 ? '' : 'subContents__content__body--basic'
                    }>
                    <Grid
                      item
                      xs={12}
                      className="subContents__content__body__data__group">
                      {trans(
                        `ViewGroup.body.label.floor.floor${convertFloorName(
                          groupInfo['floor']
                        )}`
                      )}
                    </Grid>
                    {groupInfo.groupIds.map(groupID => {
                      /** グループデータ */
                      const groupData = viewGroupData.find(info => {
                        return info['group-id'] === groupID;
                      });
                      /** 消費電力量データ */
                      const groupPwData = viewGroupPwData.find(info => {
                        return info['group-id'] === groupID;
                      });

                      if (isUndefined(groupData) || isUndefined(groupPwData)) {
                        return <Grid item xs={12} key={groupID}></Grid>;
                      }
                      return (
                        <Grid item xs={12} key={groupID}>
                          <GroupView
                            siteID={siteId}
                            floor={groupInfo.floor}
                            groupData={groupData}
                            isCommand={
                              groupID in groupAuthority &&
                              groupAuthority[groupID] === 'manager'
                            }
                            groupPowerData={groupPwData}
                            selectedGroup={selectedGroup}
                            setSelectedGroup={setSelectedGroup}
                            setGraphParam={props.setGraphParam}
                            setOperationParam={props.setOperationParam}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              })}
          </Stack>
        </Box>
      </>
    );
  }
);

/**
 * フィルタの適用情報の表示 押下でフィルタキャンセル
 * @param props props
 * - floorFilter 適用しているフロア
 * @return JSX
 */
const FilterButton = memo((props: {floorFilter: string}) => {
  /** フィルタ状態をセットする関数 */
  const {applyFilter} = useContext(ContextApplyFilterTab);

  const trans = useTranslation().t; //翻訳関数

  if (props.floorFilter === '') return <></>;
  return (
    //フィルタの解除ボタン
    <Chip
      deleteIcon={
        <img
          src={iconRed.CLOSE.src}
          alt={`${trans(
            `ViewGroup.body.image.alternativeText.${iconRed.CLOSE.alt}`
          )}`}
          className="subContents__content__filterChip__icon"
        />
      }
      label={
        <Box
          title={`Floor ${props.floorFilter.replace('-', 'B')}`}
          className="subContents__content__filterChip__text">
          {trans(
            `ViewGroup.buttonTxt.filter.floor.floor${props.floorFilter.replace(
              '-',
              'B'
            )}`
          )}
        </Box>
      }
      onDelete={() => {
        applyFilter({floor: '', groupID: '', otherUnitType: ''});
      }}
      variant="outlined"
      id="mv-groupsClearFilterBtn"
    />
  );
});

/**
 * フロア内のグループ一覧
 * @param props props
 * - siteID サイトID
 * - floor 階層
 * - groupData グループの状態値
 * - isCommand ユーザがグループに指令ができるかどうか
 * - groupPowerData グループの消費電力量
 * - selectedGroup 選択状態のグループ
 * - setSelectedGroup 選択状態のグループのset関数
 * - setGraphParam グラフのパラメータのset関数
 * - setOperationParam 指令のパラメータのset関数
 * @returns JSX
 */
const GroupView = memo(
  (props: {
    siteID: string;
    floor: string;
    groupData: apiIF.resParameters.groupStates;
    isCommand: boolean;
    groupPowerData: mysite.groupPwData;
    selectedGroup: string;
    setSelectedGroup: React.Dispatch<React.SetStateAction<string>>;
    setGraphParam: (
      graphTarget: GraphTargetType,
      graphTargetID?: string,
      graphType?: GraphFormType,
      graphTitle?: string
    ) => void;
    setOperationParam: (
      target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined,
      targetID: string,
      siteID: string
    ) => void;
  }) => {
    const navigate = useNavigate();
    const trans = useTranslation().t; //翻訳関数
    /** フィルタ、タブ状態をセットする関数 */
    const {applyFilter, applyTab} = useContext(ContextApplyFilterTab);

    //ホバー状態のグループ
    const [hoveringGroup, setHoveringGroup] = useState('');
    //prettier-ignore
    const [hoveringIcon, setHoveringIcon] = useState<'' | 'graph' | 'control'>(''); //ホバー状態のアイコン

    /** 選択されているかどうか */
    const isSelected = props.groupData['group-id'] === props.selectedGroup;
    /** ホバーされているかどうか */
    const isHover = props.groupData['group-id'] === hoveringGroup;
    /** エラーユニットフラグ エラー有:true エラー無:false */
    const errUnitFlg = props.groupData['malfunctioning-number'] !== '0';

    /**
     * グループが選択状態か判定
     * @param groupID グループID
     */
    const selectHandle = (groupID: string) => {
      if (groupID === props.selectedGroup) {
        props.setSelectedGroup('');
      } else {
        props.setSelectedGroup(groupID);
      }
    };

    return (
      <Grid
        container
        alignItems="center"
        className={`subContents__content__body__data ${
          isSelected ? 'subContents__content__body__data--selected' : ''
        }`}
        onClick={() => selectHandle(props.groupData['group-id'])}
        onMouseEnter={() => setHoveringGroup(props.groupData['group-id'])}
        onMouseLeave={() => setHoveringGroup('')}>
        {/** グループ名 */}
        <Grid
          item
          xs={gridXsAirGroup}
          title={props.groupData['group-name']}
          className="subContents__content__body__data__category">
          {props.groupData['group-name']}
        </Grid>
        {/** ユニットアイコン、運転台数/登録台数、異常表示 */}
        <Grid
          item
          xs={gridXsAirUnit}
          container
          columnSpacing={1}
          alignItems="center">
          {/** ユニットアイコン */}
          <Grid item xs="auto">
            <Box className="subContents__content__body__data__status__unitIcon__border">
              <img
                src={iconBlack.AC_ON.src}
                alt={`${trans(
                  `ViewGroup.body.image.alternativeText.${iconBlack.AC_ON.alt}`
                )}`}
                className="subContents__content__body__data__status__unitIcon__border__image"
              />
            </Box>
          </Grid>
          {/** 運転台数/登録台数 */}
          <Grid item xs="auto">
            {props.groupData['operating-number'] !== undefined &&
              props.groupData['register-number'] !== undefined && (
                <>
                  <Box
                    component="span"
                    className="subContents__content__body__data__status__operatingNumber">
                    {props.groupData['operating-number'].padStart(3, '0')}
                  </Box>
                  /{props.groupData['register-number'].padStart(3, '0')}
                </>
              )}
          </Grid>
          {/** 異常表示 */}
          {/** 異常時のみ表示する */}
          {errUnitFlg && (
            <Grid item xs="auto">
              <Stack alignItems="center" spacing={0.5}>
                <img
                  src={iconWhite.ERROR.src}
                  alt={`${trans(
                    `ViewGroup.body.image.alternativeText.${iconWhite.ERROR.alt}`
                  )}`}
                  className="subContents__content__body__data__status__errorIcon__icon"
                />
                <Box
                  component="span"
                  className="subContents__content__body__data__status__errorIcon__text">
                  {props.groupData['malfunctioning-number']}
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
        {/** 消費電力量、グラフ、機器操作 */}
        <Grid
          item
          xs={gridXsAirKwh}
          container
          justifyContent="flex-end"
          className="subContents__content__body__data__powerGraphOpe">
          {/** 消費電力量 */}
          <Grid
            item
            xs="auto"
            className={`subContents__content__body__data__powerGraphOpe__power ${
              isSelected
                ? 'subContents__content__body__data--selected__powerGraphOpe__power'
                : ''
            }`}>
            {numberWithPeriod(
              roundOff(props.groupPowerData['power-consumption'])
            )}
          </Grid>
          <Grid
            item
            xs="auto"
            className={`subContents__content__body__data__powerGraphOpe__KWh ${
              isSelected
                ? 'subContents__content__body__data--selected__powerGraphOpe__KWh'
                : ''
            }`}>
            {trans('ViewGroup.body.unit')}
          </Grid>
          {/** グラフ、機器操作 */}
          <Grid
            item
            xs="auto"
            className="subContents__content__body__data__powerGraphOpe__graphOpeIcon">
            <GraphButton
              isHover={isHover && hoveringIcon === 'graph'}
              setHoveringIcon={setHoveringIcon}
              isIconDisabled={props.groupData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットのみが含まれたグループなので無効化
              onClick={() => {
                props.setGraphParam(
                  'groups',
                  props.groupData['group-id'],
                  'bar',
                  props.groupData['group-name']
                );
              }}
            />
            {props.isCommand && (
              <ControlButton
                isHover={isHover && hoveringIcon === 'control'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={props.groupData['error-code'] === ''} //エラーコードが空文字の場合、無課金GWのユニットのみが含まれたグループなので無効化
                onClick={() => {
                  props.setOperationParam(
                    'groups',
                    props.groupData['group-id'],
                    props.siteID
                  );
                }}
              />
            )}
          </Grid>
        </Grid>
        {/** 矢印 */}
        <Grid item xs={gridXsAirArrow}>
          <IconButton
            onClick={() => {
              applyFilter({
                floor: props.floor,
                groupID: props.groupData['group-id'],
                otherUnitType: '',
              });
              applyTab('units');
              navigate(
                `/pages/my-site/site-detail/${props.siteID}/state/group/${props.groupData['group-id']}`
              );
            }}>
            <img
              src={
                isHover && hoveringIcon === ''
                  ? iconWhite.CHEVRON_RIGHT.src //グループをホバー中 かつ グラフ・機器操作アイコンをホバー中
                  : iconBlack.CHEVRON_RIGHT.src
              }
              alt={`${trans(
                'ViewGroup.body.image.alternativeText.arrowRight'
              )}`}
              className="subContents__content__body__data__arrow"
            />
          </IconButton>
        </Grid>
      </Grid>
    );
  }
);

export default ViewGroup;
